import { useState, useMemo, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle2,
  ArrowLeft,
  ArrowRight,
  Save,
  Upload,
  FileCheck,
  FileDown,
  ArrowUpRight,
  Zap,
  X
} from 'lucide-react';

const STEPS = [
  'Thông tin chung',
  'Tự khai 27 KPI',
  'Bằng chứng & Giải trình',
  'Tổng hợp & Nộp'
];

// ─── KPI Item type (autoVal và naOk là optional — chỉ có ở một số KPI) ─────
interface KpiItem {
  code: string;
  name: string;
  w: number;
  auto: boolean;
  autoVal?: number;   // Giá trị thu tự động (cron 02:00) — chỉ khi auto=true
  naOk?: boolean;     // KPI này có thể được đánh dấu N/A
}

// 8 Tiêu chí / 27 KPI (Phụ lục II v3.1 · NĐ 278/2025)
const TC_GROUPS: Array<{
  tc: string;
  name: string;
  weight: number;
  color: string;
  naGroup?: boolean;
  naLabel?: string;
  kpis: KpiItem[];
}> = [
  { tc: 'TC1', name: 'Pháp lý & Công bố', weight: 15, color: 'emerald',
    kpis: [
      { code: 'TC1a', name: 'Tỷ lệ CSDL đã công bố / toàn bộ CSDL (%)', w: 30, auto: true,  autoVal: 78 },
      { code: 'TC1b', name: 'Danh mục điện tử đã công bố theo quy định', w: 40, auto: false },
      { code: 'TC1c', name: 'Văn bản quản lý dữ liệu nội bộ ban hành', w: 30, auto: false },
    ]
  },
  { tc: 'TC2', name: 'Khai thác Dữ liệu', weight: 10, color: 'indigo',
    kpis: [
      { code: 'TC2a', name: 'API khai thác theo chuẩn NDOP', w: 40, auto: false },
      { code: 'TC2b', name: 'DVCTT sử dụng dữ liệu từ HTQLNN [N/A nếu không có DVCTT]', w: 30, auto: false, naOk: true },
      { code: 'TC2c', name: 'Chia sẻ, khai thác dữ liệu liên ngành', w: 30, auto: false },
    ]
  },
  { tc: 'TC3', name: 'DVC Trực tuyến', weight: 10, color: 'violet',
    naGroup: true, naLabel: 'N/A nếu không có DVC Trực tuyến',
    kpis: [
      { code: 'TC3a', name: 'Tỷ lệ DVCTT tích hợp cổng IGATE', w: 50, auto: false },
      { code: 'TC3b', name: 'Tỷ lệ dịch vụ công mức 3–4', w: 50, auto: false },
    ]
  },
  { tc: 'TC4', name: 'Chất lượng Dữ liệu', weight: 15, color: 'cyan',
    kpis: [
      { code: 'TC4a', name: 'Tỷ lệ dữ liệu đầy đủ — completeness (%)', w: 30, auto: true, autoVal: 88 },
      { code: 'TC4b', name: 'Tỷ lệ dữ liệu sạch — cleanliness (%)', w: 30, auto: false },
      { code: 'TC4c', name: 'Tuân thủ từ điển dữ liệu QĐ 2439 (%)', w: 20, auto: true, autoVal: 91 },
      { code: 'TC4d', name: 'Kế hoạch xử lý dữ liệu không đúng định dạng', w: 20, auto: false },
    ]
  },
  { tc: 'TC5', name: 'An toàn Thông tin', weight: 15, color: 'rose',
    kpis: [
      { code: 'TC5a', name: 'Số lỗ hổng CVSS ≥ 7.0 chưa vá (< 30 ngày)', w: 40, auto: true, autoVal: 8 },
      { code: 'TC5b', name: 'MTTR sự cố bảo mật (giờ)', w: 30, auto: true, autoVal: 7 },
      { code: 'TC5c', name: 'Cấp độ ATTT được phê duyệt [N/A nếu CSDL Cấp 1–2]', w: 30, auto: false, naOk: true },
    ]
  },
  { tc: 'TC6', name: 'Kết nối & Chia sẻ', weight: 10, color: 'blue',
    kpis: [
      { code: 'TC6a', name: 'Tỷ lệ kết nối thành công với NDOP (%)', w: 40, auto: true, autoVal: 96 },
      { code: 'TC6b', name: 'MTTR cảnh báo SOC (giờ)', w: 30, auto: true, autoVal: 3 },
      { code: 'TC6c', name: 'Số lượng kết nối liên thông qua NDOP', w: 30, auto: false },
    ]
  },
  { tc: 'TC7', name: 'Kiểm toán Độc lập', weight: 15, color: 'amber',
    kpis: [
      { code: 'TC7a', name: 'Đã thực hiện kiểm toán độc lập theo quy định', w: 50, auto: false },
      { code: 'TC7b', name: 'Số kiến nghị từ kỳ trước chưa xử lý (0 = tốt)', w: 30, auto: false },
      { code: 'TC7c', name: 'Báo cáo sau kiểm toán đã được phê duyệt', w: 20, auto: false },
    ]
  },
  { tc: 'TC8', name: 'Quản trị & Vận hành', weight: 10, color: 'slate',
    kpis: [
      { code: 'TC8a', name: 'Nhân lực quản lý dữ liệu được phân công chính thức', w: 40, auto: false },
      { code: 'TC8b', name: 'Quy trình nghiệp vụ xử lý dữ liệu đã ban hành', w: 30, auto: false },
      { code: 'TC8c', name: 'Đào tạo định kỳ về quản lý dữ liệu cho cán bộ', w: 30, auto: false },
    ]
  },
];

// Flatten all KPIs
const ALL_KPIS = TC_GROUPS.flatMap(g => g.kpis);

function autoScore(kpi: KpiItem): number {
  if (!kpi.auto || kpi.autoVal === undefined) return 0;
  const v = kpi.autoVal;
  if (kpi.code === 'TC5a') return v === 0 ? 10 : v <= 2 ? 7 : v <= 5 ? 5 : 0;
  if (kpi.code === 'TC5b' || kpi.code === 'TC6b') return v <= 1 ? 10 : v <= 4 ? 8 : v <= 8 ? 6 : v <= 24 ? 3 : 0;
  return v >= 95 ? 10 : v >= 80 ? 7 : v >= 60 ? 5 : v >= 40 ? 3 : 0;
}

export default function PortalReportingForm() {
  const [currentStep, setCurrentStep] = useState(1);
  const [kpiScores, setKpiScores] = useState<Record<string, number>>(() => {
    const init: Record<string, number> = {};
    ALL_KPIS.forEach(k => { init[k.code] = k.auto ? autoScore(k) : 0; });
    return init;
  });
  const [kpiNa, setKpiNa] = useState<Record<string, boolean>>({});
  // kpiNotes: reserved for Step 3 (Bằng chứng & Giải trình) — sẽ dùng khi implement Step 3
  const [_kpiNotes, _setKpiNotes] = useState<Record<string, string>>({});
  const [tc3Na, setTc3Na] = useState(false);
  const [formData, setFormData] = useState({
    unitName: 'Sở Tài chính TP. Hà Nội',
    responsiblePerson: 'Nguyễn Văn A',
    sessionCode: 'KT-2026-STC-001',
    cycleType: 'FOLLOW' as 'FIRST' | 'FOLLOW',
    period: 'Quý II/2026',
    importance: 'IMPORTANT',
  });

  // ─── Upload minh chứng ────────────────────────────────────────────────────
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const ACCEPTED_TYPES = '.pdf,.doc,.docx,.xlsx,.xls,.png,.jpg,.jpeg';
  const MAX_FILE_MB = 20;

  const addFiles = useCallback((newFiles: FileList | File[]) => {
    const arr = Array.from(newFiles).filter(f => {
      if (f.size > MAX_FILE_MB * 1024 * 1024) {
        alert(`Tệp "${f.name}" vượt quá ${MAX_FILE_MB}MB.`);
        return false;
      }
      return true;
    });
    setUploadedFiles(prev => {
      const names = new Set(prev.map(f => f.name));
      return [...prev, ...arr.filter(f => !names.has(f.name))];
    });
  }, []);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) addFiles(e.target.files);
    e.target.value = '';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files) addFiles(e.dataTransfer.files);
  };

  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Tính điểm tổng hợp
  const score = useMemo(() => {
    let total = 0;
    let totalW = 0;
    TC_GROUPS.forEach(g => {
      if (tc3Na && g.tc === 'TC3') return;
      const tcW = g.weight + (tc3Na && (g.tc === 'TC4' || g.tc === 'TC5') ? 5 : 0);
      let tcScore = 0; let activeW = 0;
      g.kpis.forEach(k => {
        if (kpiNa[k.code]) return;
        const s = kpiScores[k.code] ?? 0;
        tcScore += s * k.w; activeW += k.w;
      });
      if (activeW > 0) { tcScore = (tcScore / activeW); }
      total += tcScore * tcW; totalW += tcW * 10;
    });
    return totalW > 0 ? Math.round((total / totalW) * 100) : 0;
  }, [kpiScores, kpiNa, tc3Na]);

  const tc7bScore = kpiScores['TC7b'] ?? 0;
  const ranking = useMemo(() => {
    if (score >= 80 && tc7bScore === 0) return { label: 'A — Xuất sắc', code: 'A', color: 'text-emerald-700 dark:text-emerald-400 bg-emerald-500/10 border-emerald-500/30' };
    if (score >= 70) return { label: 'B-Tốt', code: 'B_TOT', color: 'text-blue-700 dark:text-blue-400 bg-blue-500/10 border-blue-500/30' };
    if (score >= 60) return { label: 'B-Đạt', code: 'B_DAT', color: 'text-indigo-700 dark:text-indigo-400 bg-indigo-500/10 border-indigo-500/30' };
    return { label: 'C — Không đạt', code: 'C', color: 'text-rose-700 dark:text-rose-400 bg-rose-500/10 border-rose-500/30' };
  }, [score, tc7bScore]);

  const totalSelfAssessed = ALL_KPIS.filter(k => !k.auto && !kpiNa[k.code] && kpiScores[k.code] > 0).length;
  const totalNa = Object.values(kpiNa).filter(Boolean).length + (tc3Na ? 2 : 0);

  const inheritFromPrevious = () => {
    const next = { ...kpiScores };
    ALL_KPIS.filter(k => !k.auto).forEach(k => { next[k.code] = 7; });
    setKpiScores(next);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700 pb-20 px-4">
      {/* Page Navigation */}
      <div className="flex items-center justify-between">
         <button className="flex items-center gap-2 text-slate-700 hover:text-slate-950 dark:text-slate-400 dark:hover:text-white transition-colors group px-4 py-2 rounded-xl hover:bg-slate-100 dark:hover:bg-white/5">
            <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
            Quay lại Lịch sử
         </button>
         <div className="flex gap-4">
            {currentStep === 2 && (
               <button
                  onClick={inheritFromPrevious}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-purple-500/10 border border-purple-500/30 text-purple-700 dark:text-purple-400 text-sm font-bold hover:bg-purple-500/20 transition-all"
               >
                  <ArrowUpRight size={16} />
                  Kế thừa KPI kỳ trước (FOLLOW)
               </button>
            )}
            <button className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-950 dark:text-slate-100 text-sm font-bold hover:bg-slate-200 dark:hover:bg-white/10 transition-all">
               <Save size={16} />
               Lưu bản nháp
            </button>
            <button className="flex items-center gap-2 px-4 py-2 rounded-xl border border-cyan-500/30 text-cyan-700 dark:text-cyan-400 text-sm font-bold hover:bg-cyan-500/10 transition-all">
               <FileDown size={16} />
               Xuất Phiếu KPI (Word)
            </button>
         </div>
      </div>

      {/* Stepper */}
      <div className="glass-panel p-6 flex items-center justify-between overflow-hidden relative border-slate-200 dark:border-white/5 bg-white dark:bg-white/[0.02] shadow-sm">
         {STEPS.map((step, idx) => {
           const stepNum = idx + 1;
           const active = currentStep === stepNum;
           const completed = currentStep > stepNum;
           return (
             <div key={step} className="flex-1 flex items-center relative z-10">
                <div className="flex flex-col items-center gap-2 w-full">
                   <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-heading font-bold transition-all ${
                        active 
                        ? 'bg-cyan-500 text-white shadow-[0_0_20px_rgba(6,182,212,0.4)] scale-110' 
                        : completed 
                        ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30' 
                        : 'bg-slate-200 dark:bg-white/10 text-slate-600 dark:text-slate-400 border border-slate-300 dark:border-white/20'
                   }`}>
                      {completed ? <CheckCircle2 size={24} /> : stepNum}
                   </div>
                   <span className={`text-[10px] font-black tracking-widest ${active ? 'text-cyan-700 dark:text-cyan-400' : completed ? 'text-emerald-700/70 dark:text-emerald-400/70' : 'text-slate-500 dark:text-slate-500'}`}>
                      {step}
                   </span>
                </div>
                {idx < STEPS.length - 1 && (
                   <div className="absolute left-[calc(50%+20px)] right-[-20px] top-[20px] h-[1px] bg-slate-200 dark:bg-white/5">
                      <motion.div 
                        initial={{ width: 0 }}
                        animate={{ width: completed ? '100%' : '0%' }}
                        className="h-full bg-emerald-500/40"
                      />
                   </div>
                )}
             </div>
           );
         })}
      </div>

      {/* Main Content Area */}
      <div className="glass-panel p-8 border-slate-200 dark:border-white/5 bg-white dark:bg-white/[0.01] relative min-h-[500px]">
         <AnimatePresence mode="wait">
            {currentStep === 1 && (
               <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  key="step1"
                  className="space-y-6"
               >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                     <div className="space-y-6">
                        <h3 className="text-xl font-heading font-black text-slate-950 dark:text-white flex items-center gap-3">
                          <div className="w-1.5 h-8 bg-cyan-500 rounded-full shadow-[0_0_15px_rgba(6,182,212,0.5)]" />
                          THÔNG TIN PHIÊN KIỂM TOÁN
                        </h3>
                        <div className="space-y-4">
                           <div className="space-y-1">
                              <label className="text-[10px] font-bold text-slate-700 dark:text-slate-400 tracking-widest leading-loose">Tên đơn vị báo cáo</label>
                              <div className="bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-slate-950 dark:text-slate-100 font-medium">{formData.unitName}</div>
                           </div>
                           <div className="space-y-1">
                              <label className="text-[10px] font-bold text-slate-700 dark:text-slate-400 tracking-widest leading-loose">Mã phiên kiểm toán</label>
                              <div className="bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 font-mono text-cyan-600 dark:text-cyan-400 text-sm font-black">{formData.sessionCode}</div>
                           </div>
                           <div className="space-y-1">
                              <label className="text-[10px] font-bold text-slate-700 dark:text-slate-400 tracking-widest leading-loose">Người chịu trách nhiệm chính</label>
                              <input
                                 type="text"
                                 value={formData.responsiblePerson}
                                 onChange={(e: any) => setFormData({...formData, responsiblePerson: e.target.value})}
                                 className="w-full bg-slate-50 dark:bg-black/40 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-slate-950 dark:text-white outline-none focus:border-cyan-500/50 transition-colors shadow-inner"
                              />
                           </div>
                           <div className="space-y-2">
                              <label className="text-[10px] font-bold text-slate-700 dark:text-slate-400 tracking-widest leading-loose">Loại chu kỳ đánh giá</label>
                              <div className="flex gap-2">
                                 {(['FIRST', 'FOLLOW'] as const).map(t => (
                                    <button key={t} onClick={() => setFormData({...formData, cycleType: t})}
                                       className={`flex-1 py-3 rounded-xl border text-[10px] font-black transition-all ${
                                          formData.cycleType === t
                                          ? 'bg-cyan-500/10 text-cyan-700 dark:text-cyan-400 border-cyan-500/40'
                                          : 'bg-slate-50 dark:bg-white/5 text-slate-700 dark:text-slate-500 border-slate-200 dark:border-white/5'
                                       }`}>
                                       {t === 'FIRST' ? '🔵 FIRST — Kỳ đầu tiên' : '🟢 FOLLOW — Kỳ tiếp theo'}
                                    </button>
                                 ))}
                              </div>
                              {formData.cycleType === 'FIRST' && (
                                 <p className="text-[10px] text-amber-600 dark:text-amber-400 font-bold">⚠ TC7 tối đa 5.0/10 ở kỳ đầu tiên</p>
                              )}
                           </div>
                        </div>
                     </div>

                     <div className="space-y-6">
                        <h3 className="text-xl font-heading font-black text-slate-950 dark:text-white flex items-center gap-3">
                           <div className="w-1.5 h-8 bg-purple-500 rounded-full shadow-[0_0_15px_rgba(168,85,247,0.5)]" />
                           CẤU HÌNH N/A
                        </h3>
                        <div className="space-y-4">
                           <div className="p-4 rounded-xl border border-amber-500/20 bg-amber-500/[0.03] space-y-3">
                              <p className="text-[10px] font-black text-amber-600 dark:text-amber-400 tracking-widest">Quy tắc N/A — Phân bổ lại trọng số</p>
                              <label className="flex items-center gap-3 cursor-pointer group">
                                 <input type="checkbox" checked={tc3Na} onChange={e => setTc3Na(e.target.checked)}
                                    className="w-4 h-4 accent-amber-500" />
                                 <div>
                                    <p className="text-xs font-bold text-slate-900 dark:text-white">TC3 = N/A</p>
                                    <p className="text-[10px] text-slate-500">Không có DVC Trực tuyến → +5% TC4, +5% TC5</p>
                                 </div>
                              </label>
                           </div>
                           <div className="p-4 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/[0.02] space-y-2">
                              <p className="text-[10px] font-black text-slate-500 tracking-widest">7 KPI thu thập tự động (cron 02:00)</p>
                              {ALL_KPIS.filter(k => k.auto).map(k => (
                                 <div key={k.code} className="flex items-center justify-between">
                                    <span className="text-xs text-slate-600 dark:text-slate-400">{k.code}</span>
                                    <span className="flex items-center gap-1 text-[10px] font-black text-emerald-600 dark:text-emerald-400">
                                       <Zap size={10} /> AUTO · {k.autoVal} → {autoScore(k)}/10
                                    </span>
                                 </div>
                              ))}
                           </div>
                        </div>
                     </div>
                  </div>
               </motion.div>
            )}

            {currentStep === 2 && (
               <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  key="step2"
                  className="space-y-6"
               >
                  <div className="flex flex-col md:flex-row md:items-end justify-between border-b border-slate-100 dark:border-white/5 pb-6 gap-4">
                     <div>
                        <h3 className="text-3xl font-heading font-black text-slate-900 dark:text-white tracking-tight">
                           02. Tự khai báo KPI (8 TC / 27 KPI)
                        </h3>
                        <p className="text-slate-500 text-xs mt-1 font-medium">
                           Thang điểm 0 / 3 / 5 / 7 / 10 · 7 KPI tự động <span className="text-emerald-600 dark:text-emerald-400">(AUTO)</span> · 20 KPI tự khai
                        </p>
                     </div>
                     <div className="text-right glass-panel px-6 py-3 border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/[0.02]">
                        <p className="text-[9px] font-black text-slate-500 dark:text-slate-400 tracking-[0.2em] mb-1">Điểm Preview</p>
                        <div className="flex items-center gap-4">
                           <span className="text-4xl font-heading font-black text-cyan-600 dark:text-cyan-400">{score}</span>
                           <div className="w-48 h-2 bg-slate-200 dark:bg-white/5 rounded-full overflow-hidden border border-slate-300 dark:border-white/10">
                              <motion.div
                                 initial={{ width: 0 }}
                                 animate={{ width: `${score}%` }}
                                 className="h-full bg-gradient-to-r from-cyan-500 to-purple-500 shadow-[0_0_15px_rgba(6,182,212,0.6)]"
                              />
                           </div>
                        </div>
                     </div>
                  </div>

                  {/* KPI groups by TC */}
                  <div className="space-y-6">
                     {TC_GROUPS.map(g => {
                       if (tc3Na && g.tc === 'TC3') return null;
                       return (
                         <div key={g.tc} className={`rounded-2xl border border-${g.color}-200 dark:border-${g.color}-500/20 overflow-hidden`}>
                           <div className={`flex items-center justify-between px-5 py-3 bg-${g.color}-50 dark:bg-${g.color}-500/10`}>
                             <div className="flex items-center gap-3">
                               <span className={`font-mono text-[10px] font-black px-2 py-0.5 rounded bg-${g.color}-500/20 text-${g.color}-700 dark:text-${g.color}-400`}>{g.tc}</span>
                               <span className="text-sm font-black text-slate-900 dark:text-white">{g.name}</span>
                               <span className="text-[10px] text-slate-500">trọng số {g.weight}%</span>
                             </div>
                             {g.naGroup && (
                               <span className="text-[9px] font-black text-amber-600 dark:text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded">{g.naLabel}</span>
                             )}
                           </div>
                           <table className="w-full text-left">
                             <thead>
                               <tr className="border-b border-slate-100 dark:border-white/5 bg-slate-50/50 dark:bg-white/[0.01]">
                                 <th className="px-4 py-2 text-[10px] font-black text-slate-500 tracking-widest w-20">KPI</th>
                                 <th className="px-4 py-2 text-[10px] font-black text-slate-500 tracking-widest">Chỉ tiêu</th>
                                 <th className="px-4 py-2 text-[10px] font-black text-slate-500 tracking-widest text-center w-16">W%</th>
                                 <th className="px-4 py-2 text-[10px] font-black text-slate-500 tracking-widest text-center w-52">Điểm (0/3/5/7/10)</th>
                                 <th className="px-4 py-2 text-[10px] font-black text-slate-500 tracking-widest w-28 text-center">N/A</th>
                               </tr>
                             </thead>
                             <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                               {g.kpis.map(k => {
                                 const isNa = !!kpiNa[k.code];
                                 const curScore = kpiScores[k.code] ?? 0;
                                 return (
                                   <tr key={k.code} className={`hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-all ${isNa ? 'opacity-40' : ''}`}>
                                     <td className="px-4 py-3">
                                       <div className="flex items-center gap-1.5">
                                         <span className="font-mono text-xs font-black text-slate-700 dark:text-slate-300">{k.code}</span>
                                         {k.auto && <span className="text-[8px] font-black text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded">AUTO</span>}
                                       </div>
                                     </td>
                                     <td className="px-4 py-3 text-xs text-slate-700 dark:text-slate-300 font-medium">{k.name}</td>
                                     <td className="px-4 py-3 text-center text-[10px] font-black text-slate-500">{k.w}%</td>
                                     <td className="px-4 py-3">
                                       {k.auto ? (
                                         <div className="flex items-center justify-center gap-2">
                                           <Zap size={12} className="text-emerald-500" />
                                           <span className="text-sm font-black text-emerald-600 dark:text-emerald-400">{curScore}/10</span>
                                           <span className="text-[9px] text-slate-400">(raw: {k.autoVal})</span>
                                         </div>
                                       ) : (
                                         <div className="flex justify-center items-center gap-1">
                                           {[0, 3, 5, 7, 10].map(v => (
                                             <button key={v} disabled={isNa}
                                               onClick={() => setKpiScores(prev => ({...prev, [k.code]: v}))}
                                               className={`w-8 h-8 rounded-lg text-[11px] font-black border transition-all disabled:opacity-30 ${
                                                 curScore === v && !isNa
                                                 ? 'bg-cyan-500 text-white border-cyan-500 shadow-[0_0_12px_rgba(6,182,212,0.3)]'
                                                 : 'bg-slate-100 dark:bg-white/5 text-slate-500 border-slate-200 dark:border-white/5 hover:bg-slate-200 dark:hover:bg-white/10'
                                               }`}>{v}</button>
                                           ))}
                                         </div>
                                       )}
                                     </td>
                                     <td className="px-4 py-3 text-center">
                                       {k.naOk && (
                                         <button onClick={() => setKpiNa(prev => ({...prev, [k.code]: !prev[k.code]}))}
                                           className={`text-[10px] font-black px-3 py-1 rounded-lg border transition-all ${
                                             isNa
                                             ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30'
                                             : 'text-slate-400 border-slate-200 dark:border-white/10 hover:border-amber-500/30'
                                           }`}>
                                           {isNa ? 'N/A ✓' : 'N/A'}
                                         </button>
                                       )}
                                     </td>
                                   </tr>
                                 );
                               })}
                             </tbody>
                           </table>
                         </div>
                       );
                     })}
                  </div>
               </motion.div>
            )}

            {currentStep === 3 && (
               <motion.div 
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  key="step3" 
                  className="space-y-8"
               >
                  <div>
                     <h3 className="text-3xl font-heading font-black text-slate-950 dark:text-white tracking-tight">03. Minh chứng & Hồ sơ Đính kèm</h3>
                     <p className="text-slate-500 text-xs mt-1 font-medium">Hệ thống yêu cầu upload tài liệu PDF cho các tiêu chí đánh giá "Đạt". Tối đa 20MB/file.</p>
                  </div>

                  {/* Hidden file input */}
                  <input
                     ref={fileInputRef}
                     type="file"
                     multiple
                     accept={ACCEPTED_TYPES}
                     className="hidden"
                     onChange={handleFileInputChange}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                     {/* Upload drop zone */}
                     <div className="space-y-4">
                        <label className="text-[10px] font-bold text-slate-700 dark:text-slate-400 tracking-[0.2em] flex items-center gap-2">
                           Bắt buộc: Kế hoạch & Chính sách dữ liệu
                           <div className="w-1.5 h-1.5 rounded-full bg-rose-500 shadow-[0_0_5px_rgba(244,63,94,0.5)]" />
                        </label>
                        <div
                           onClick={() => fileInputRef.current?.click()}
                           onDragOver={handleDragOver}
                           onDragLeave={handleDragLeave}
                           onDrop={handleDrop}
                           className={`border-2 border-dashed rounded-3xl p-12 flex flex-col items-center justify-center gap-4 transition-all cursor-pointer group shadow-inner ${
                              isDragging
                                 ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-500/10 scale-[1.01]'
                                 : 'border-slate-200 dark:border-white/5 bg-slate-50 dark:bg-white/[0.01] hover:bg-slate-100 dark:hover:bg-white/[0.03] hover:border-cyan-500/30'
                           }`}
                        >
                           <div className={`w-16 h-16 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-600 dark:text-cyan-400 transition-transform ${isDragging ? 'scale-110' : 'group-hover:scale-110'}`}>
                              <Upload size={28} />
                           </div>
                           <div className="text-center">
                              <p className="text-sm font-bold text-slate-950 dark:text-white tracking-tight">
                                 {isDragging ? 'Thả tệp vào đây...' : 'Kéo thả minh chứng vào đây'}
                              </p>
                              <p className="text-[10px] text-slate-500 dark:text-slate-500 mt-1 font-black tracking-widest leading-loose">
                                 Hoặc click để chọn tệp tin
                              </p>
                              <p className="text-[9px] text-slate-400 dark:text-slate-600 mt-2 tracking-wide">
                                 PDF, DOC, DOCX, XLSX, JPG, PNG · Tối đa {MAX_FILE_MB}MB/file
                              </p>
                           </div>
                        </div>
                     </div>

                     {/* Uploaded files list */}
                     <div className="space-y-4">
                        <label className="text-[10px] font-bold text-slate-700 dark:text-slate-400 tracking-[0.2em] flex items-center gap-2">
                           Danh sách tài liệu đã tải lên
                           {uploadedFiles.length > 0 && (
                              <span className="px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 text-[9px] font-black">
                                 {uploadedFiles.length}
                              </span>
                           )}
                        </label>
                        <div className="space-y-3 min-h-[120px]">
                           {uploadedFiles.length === 0 ? (
                              <div className="flex flex-col items-center justify-center h-32 rounded-2xl border border-dashed border-slate-200 dark:border-white/5 bg-slate-50/50 dark:bg-white/[0.01] gap-2">
                                 <FileCheck size={22} className="text-slate-300 dark:text-slate-700" />
                                 <p className="text-[10px] text-slate-400 dark:text-slate-600 font-medium tracking-wide">Chưa có tài liệu nào</p>
                              </div>
                           ) : (
                              uploadedFiles.map((file, i) => (
                                 <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, x: 20 }}
                                    transition={{ delay: i * 0.05 }}
                                    key={`${file.name}-${i}`}
                                    className="flex items-center justify-between p-4 glass-panel border-slate-200 dark:border-white/10 bg-white dark:bg-white/[0.03] hover:bg-slate-50 dark:hover:bg-white/[0.06] transition-all group"
                                 >
                                    <div className="flex items-center gap-3 min-w-0">
                                       <div className="p-2.5 rounded-xl bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 shrink-0">
                                          <FileCheck size={18} />
                                       </div>
                                       <div className="min-w-0">
                                          <p className="text-[12px] font-bold text-slate-950 dark:text-white group-hover:text-cyan-700 dark:group-hover:text-cyan-300 transition-colors truncate max-w-[180px]" title={file.name}>{file.name}</p>
                                          <p className="text-[9px] text-slate-500 dark:text-slate-500 font-black tracking-widest mt-0.5">{formatFileSize(file.size)} · {file.type.split('/')[1]?.toUpperCase() || 'FILE'}</p>
                                       </div>
                                    </div>
                                    <button
                                       onClick={() => removeFile(i)}
                                       className="shrink-0 p-1.5 rounded-lg text-rose-500 opacity-40 hover:opacity-100 hover:bg-rose-500/10 transition-all ml-2"
                                       title="Gỡ bỏ"
                                    >
                                       <X size={14} />
                                    </button>
                                 </motion.div>
                              ))
                           )}
                        </div>
                     </div>
                  </div>
               </motion.div>
            )}

            {currentStep === 4 && (
               <motion.div 
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  key="step4" 
                  className="flex flex-col items-center py-12 space-y-12"
               >
                  <div className="relative">
                     <div className="absolute inset-0 bg-cyan-500/20 blur-[100px] rounded-full" />
                     <div className="relative w-48 h-48 rounded-full border border-slate-200 dark:border-white/10 flex flex-col items-center justify-center bg-white dark:bg-black/40 backdrop-blur-xl shadow-[0_0_60px_rgba(0,0,0,0.1)] dark:shadow-[0_0_60px_rgba(0,0,0,0.5)]">
                        {/* Glow Ring */}
                        <div className="absolute inset-0 rounded-full border-t-2 border-cyan-500/40 animate-spin" style={{ animationDuration: '3s' }} />
                        <p className="text-[11px] font-black text-cyan-600 dark:text-cyan-500 tracking-[0.4em] mb-1">Audit Score</p>
                        <p className="text-7xl font-heading font-black text-slate-950 dark:text-white tracking-tighter">{score}</p>
                        <p className="text-[11px] font-black text-slate-500 absolute bottom-10 tracking-widest">/ 100 PTS</p>
                     </div>
                  </div>

                  <div className="text-center space-y-4 max-w-xl">
                     <h3 className="text-4xl font-heading font-black text-slate-950 dark:text-white tracking-tight leading-tight">Tổng hợp & Nộp Phiếu KPI</h3>
                     <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed px-6 font-medium">
                        Phiên <span className="text-slate-950 dark:text-white font-bold font-mono">{formData.sessionCode}</span> · tự khai{' '}
                        <span className="text-cyan-600 dark:text-cyan-400 font-black">{totalSelfAssessed}/20 KPI</span>{' '}
                        · N/A: <span className="text-amber-600 dark:text-amber-400 font-black">{totalNa}</span>
                     </p>

                     <div className="inline-flex flex-col items-center gap-3 pt-4">
                        <p className="text-[10px] font-black text-slate-500 dark:text-slate-600 tracking-[0.3em]">Xếp hạng Preview (NĐ 278/2025)</p>
                        <div className={`px-12 py-5 rounded-3xl border-2 ${ranking.color} border-current flex flex-col items-center gap-1 shadow-[0_15px_40px_rgba(0,0,0,0.05)] dark:shadow-[0_15px_40px_rgba(0,0,0,0.4)] relative group overflow-hidden`}>
                           <div className="absolute inset-0 bg-current opacity-[0.03] group-hover:opacity-[0.08] transition-opacity" />
                           <span className="text-4xl font-heading font-black tracking-tighter">{ranking.label}</span>
                           <span className="text-[10px] font-bold tracking-[.25em] opacity-50">previewScore = {score}/100</span>
                        </div>
                        {ranking.code === 'C' && (
                          <p className="text-xs text-rose-600 dark:text-rose-400 font-bold">⚠ Kiểm tra Hard Conditions — vi phạm bất kỳ HC nào cũng → C</p>
                        )}
                     </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-2xl px-6">
                     <div className="glass-panel p-6 border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/[0.02]">
                        <p className="text-[9px] font-black text-slate-500 dark:text-slate-400 tracking-[0.3em] mb-4">Thông tin phiên kiểm toán</p>
                        <ul className="space-y-3">
                           {[
                              { label: 'Mã phiên', value: formData.sessionCode },
                              { label: 'Chu kỳ', value: formData.cycleType },
                              { label: 'Kỳ báo cáo', value: formData.period },
                              { label: 'Schema', value: 'v2.0' },
                           ].map(item => (
                              <li key={item.label} className="flex justify-between items-center bg-white dark:bg-black/40 rounded-xl px-4 py-2.5 border border-slate-100 dark:border-white/5 shadow-inner">
                                 <span className="text-[10px] text-slate-500 dark:text-slate-400 font-black tracking-widest">{item.label}</span>
                                 <span className="text-[12px] text-slate-950 dark:text-white font-black font-mono">{item.value}</span>
                              </li>
                           ))}
                        </ul>
                     </div>
                     <div className="glass-panel p-6 border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/[0.02] flex flex-col justify-center gap-5 relative overflow-hidden group">
                        <div className="absolute bottom-0 right-0 w-32 h-32 bg-cyan-500/5 blur-3xl rounded-full" />
                        <p className="text-[11px] text-slate-500 italic font-medium relative z-10 leading-relaxed">
                           "Bằng việc nhấn 'Nộp hồ sơ', đơn vị cam kết tính chính xác của dữ liệu giải trình và các tài liệu minh chứng đi kèm."
                        </p>
                        <button className="w-full py-4 rounded-2xl bg-gradient-to-r from-cyan-500 to-cyan-700 text-white font-black tracking-[0.2em] text-[13px] shadow-[0_10px_30px_rgba(6,182,212,0.3)] hover:shadow-[0_15px_40px_rgba(6,182,212,0.5)] transition-all hover:-translate-y-1 relative z-10">
                           Ký số & Nộp Hồ sơ
                        </button>
                     </div>
                  </div>
               </motion.div>
            )}
         </AnimatePresence>
      </div>

      {/* Footer Controls */}
      <div className="fixed bottom-0 left-72 right-0 bg-white/80 dark:bg-black/80 backdrop-blur-3xl border-t border-slate-200 dark:border-white/5 px-10 py-5 flex items-center justify-between z-50 shadow-[0_-20px_50px_rgba(0,0,0,0.05)] dark:shadow-[0_-20px_50px_rgba(0,0,0,0.5)]">
         <button 
            disabled={currentStep === 1}
            onClick={() => setCurrentStep(prev => prev - 1)}
            className={`flex items-center gap-3 px-8 py-3.5 rounded-2xl font-black text-[12px] tracking-widest transition-all ${
               currentStep === 1 
               ? 'text-slate-400 dark:text-slate-800 opacity-50 pointer-events-none' 
               : 'bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-white/10 border border-slate-200 dark:border-white/10'
            }`}
         >
            <ArrowLeft size={18} />
            Quay lại
         </button>

         <div className="flex gap-4">
            {currentStep < 4 ? (
               <button 
                  onClick={() => setCurrentStep(prev => prev + 1)}
                  className="flex items-center gap-3 px-12 py-3.5 rounded-2xl bg-gradient-to-r from-cyan-500 to-cyan-600 text-white font-black text-[12px] tracking-[0.2em] shadow-[0_10px_30px_rgba(6,182,212,0.3)] hover:shadow-[0_15px_40px_rgba(6,182,212,0.5)] transition-all group hover:-translate-y-1"
               >
                  Kế tiếp
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
               </button>
            ) : (
               <button 
                  className="flex items-center gap-3 px-12 py-3.5 rounded-2xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-700 dark:text-slate-400 font-bold text-[12px] tracking-widest hover:text-slate-900 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-white/10 transition-all"
               >
                  Đóng Form
               </button>
            )}
         </div>
      </div>
    </div>
  );
}
